import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  UserPlus, 
  CreditCard, 
  Loader2,
  CheckCircle,
  ExternalLink,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { useQueryClient } from '@tanstack/react-query';

interface AffiliateFormData {
  first_name: string;
  last_name: string;
  email: string;
  preferred_promo_code: string;
  paypal_email: string;
  wise_email: string;
  accept_terms: boolean;
}

interface AffiliateResult {
  id: string;
  email: string;
  promoCode?: string;
  referralLink?: string;
}

export function JoinAffiliateProgramForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess] = useState(false);
  const [affiliateResult, setAffiliateResult] = useState<AffiliateResult | null>(null);
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<AffiliateFormData>({
    first_name: '',
    last_name: '',
    email: '',
    preferred_promo_code: '',
    paypal_email: '',
    wise_email: '',
    accept_terms: false
  });

  // Pre-fill form with user data from profiles table
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Get full_name from profiles table
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, email')
            .eq('user_id', user.id)
            .single();

          // Parse full_name into first_name and last_name
          const fullName = profile?.full_name || '';
          const nameParts = fullName.trim().split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          setFormData(prev => ({
            ...prev,
            first_name: firstName,
            last_name: lastName,
            email: profile?.email || user.email || '',
          }));
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };

    loadUserData();
  }, []);

  const handleInputChange = (field: keyof AffiliateFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.accept_terms) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data: result, error } = await supabase.functions.invoke('create-rewardful-affiliate', {
        body: formData,
      });
      
      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(error.message || 'Failed to create affiliate account');
      }

      if (!result) {
        throw new Error('No response from affiliate service');
      }

      if (!result.success) {
        const errorMsg = result.error || 'Failed to create affiliate account';
        console.error('Affiliate creation failed:', errorMsg);
        throw new Error(errorMsg);
      }

      setAffiliateResult(result.affiliate);
      // Don't set isSuccess to true - go straight to paywall
      // setIsSuccess(true);
      // toast.success('Affiliate account created successfully!');
      
      // Invalidate referral stats to refresh the UI and show paywall immediately
      queryClient.invalidateQueries({ queryKey: ['referral-stats'] });
      
    } catch (error) {
      console.error('Error creating affiliate:', error);
      let errorMessage = 'Failed to create affiliate account';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      // Check if it's a duplicate email error
      if (errorMessage.includes('Email has already been taken') || errorMessage.includes('email is already registered')) {
        // Check if user already has affiliate status
        queryClient.invalidateQueries({ queryKey: ['referral-stats'] });
        toast.error('This email is already registered as an affiliate. Please refresh the page or contact support if you need assistance.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyPromoCode = async () => {
    if (!affiliateResult?.promoCode) return;
    try {
      await navigator.clipboard.writeText(affiliateResult.promoCode);
      toast.success('Promo code copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy promo code');
    }
  };

  const openAffiliatePortal = async () => {
    if (!affiliateResult?.id) return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        toast.error('Please sign in to access affiliate portal');
        return;
      }

      const ssoUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rewardful-affiliate-sso?affiliate_id=${affiliateResult.id}`;
      
      // Since this is a redirect endpoint, we can just navigate directly
      // The function will handle authentication server-side
      window.open(ssoUrl, '_blank');
      
    } catch (error) {
      console.error('Error accessing affiliate portal:', error);
      toast.error('Failed to access affiliate portal');
    }
  };

  if (isSuccess && affiliateResult) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle className="text-2xl">Welcome to Our Affiliate Program!</CardTitle>
          <CardDescription>
            Your affiliate account has been created successfully. Here are your details:
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {affiliateResult.promoCode && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">Your Promo Code</Label>
              <div className="flex gap-2">
                <Input
                  value={affiliateResult.promoCode}
                  readOnly
                  className="flex-1 font-mono text-lg"
                />
                <Button onClick={copyPromoCode} variant="outline">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Share this code with customers at checkout to earn commission
              </p>
            </div>
          )}


          <div className="flex gap-3 pt-4">
            <Button onClick={openAffiliatePortal} className="flex-1">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Affiliate Portal
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                type="text"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                type="text"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
              placeholder="your@email.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="preferred_promo_code">Preferred Promo Code</Label>
            <Input
              id="preferred_promo_code"
              type="text"
              value={formData.preferred_promo_code}
              onChange={(e) => handleInputChange('preferred_promo_code', e.target.value.toUpperCase())}
              placeholder="MYCODE10"
              maxLength={20}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to auto-generate a code. Only letters, numbers, and dashes allowed.
            </p>
          </div>

          <div className="border-t my-6"></div>

          <div className="space-y-4">
            <Label className="text-sm font-medium flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Payment Methods (Optional)
            </Label>
            
            <div className="space-y-2">
              <Label htmlFor="paypal_email">PayPal Email</Label>
              <Input
                id="paypal_email"
                type="email"
                value={formData.paypal_email}
                onChange={(e) => handleInputChange('paypal_email', e.target.value)}
                placeholder="paypal@email.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wise_email">Wise Email</Label>
              <Input
                id="wise_email"
                type="email"
                value={formData.wise_email}
                onChange={(e) => handleInputChange('wise_email', e.target.value)}
                placeholder="wise@email.com"
              />
            </div>
          </div>

          <div className="border-t my-6"></div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="accept_terms"
              checked={formData.accept_terms}
              onCheckedChange={(checked) => handleInputChange('accept_terms', !!checked)}
            />
            <Label htmlFor="accept_terms" className="text-sm">
              I accept the affiliate program terms and conditions *
            </Label>
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating Account...
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                Join Affiliate Program
              </>
            )}
          </Button>
        </form>
    </div>
  );
}